import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

import {
  cornerRemoval,
  deleteWall,
  appendWall,
  pullCorner,
  pushWallTracked,
  removeCorner,
  type Draft,
  type DraftChamfer,
  type DraftVertex,
} from "@/lib/draft";
import { chamferWallNumber, diagonalMm, displayWallNumber, usableMm } from "@/lib/fase";
import { formatMm } from "@/lib/format";
import { placeDimensions, type DimensionPlan } from "@/lib/dimensions";
import { dimensionLine, projectRun, type MmPoint, type ScreenPoint } from "@/lib/projection";

import styles from "./OutlineDraft.module.css";

/**
 * The board's own height before it has measured its box, in CSS pixels — and,
 * because the viewBox is written in the board's measured pixels, one viewBox
 * unit *is* one pixel. That is the whole reason for measuring: a fixed viewBox
 * letterboxes, and a house plan is nearly square, so a wide fixed frame drew
 * the shape at 40 % of the board with white on either side (measured in the
 * browser, 15.08.2026). It also makes the pointer arithmetic exact instead of
 * a ratio.
 *
 * The height itself is the stylesheet's: 520 px by default, the window's
 * height where the screen makes the drawing its stage (UX review 26.10.2026,
 * B-6), the container's once maximized (A3, 23.09.2026). The board draws at
 * whatever its box measures — this figure is what jsdom and the first render
 * see.
 */
const BOARD_HEIGHT_PX = 520;
const BOARD_FALLBACK_WIDTH_PX = 560;
/**
 * Room around the shape for the dimension figures. A figure stands 28 px
 * off its wall (`DIMENSION_OFFSET` plus the rotated label's own 10), so 44
 * left an edge wall's figure 16 px from the board's edge — at 390 px it read
 * as cut (UX gate round 4, #12). 56 keeps a clear 20 px around every edge
 * figure, rotated or not.
 */
export const VIEW_PADDING = 56;

/** What one arrow key press moves a wall — the same step `OutlineReview` uses. */
const STEP_MM = 10;
/** Millimetres the pointer snaps to, so a hand-drawn shape still lands on round figures. */
const SNAP_MM = 10;
/** Half the width of the invisible band a wall is grabbed by. */
const GRAB_HALF_WIDTH = 7;
/**
 * How close, in screen pixels, a pushed wall has to come to the line of the
 * wall beyond its neighbour to land on it — flush, the step gone
 * (`pushWall`'s `snapMm`). Pixels, not millimetres: the line has to be hit
 * with a mouse whatever the drawing's scale.
 */
const FLUSH_SNAP_PX = 8;
/** How far a slant's figure sits off it (the walls use `lib/dimensions.ts`). */
const DIMENSION_OFFSET = 18;
/** One figure digit in the board's 11 px mono face, for placing it (`lib/dimensions.ts`). */
const DIGIT_PX = 6.7;
/** The „gelesen“ dot and its gap in front of a figure. */
const SEEN_DOT_PX = 9;
/** Half the length of a dimension line's end stroke. */
const TICK_PX = 3.5;

/** How far a landmark sits off its wall, inwards — the side the figure never uses. */
const LANDMARK_OFFSET = 15;

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function boundsOf(vertices: DraftVertex[]): Bounds {
  const xs = vertices.map((vertex) => vertex.xMm);
  const ys = vertices.map((vertex) => vertex.yMm);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

function union(a: Bounds, b: Bounds): Bounds {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

function snap(valueMm: number): number {
  return Math.round(valueMm / SNAP_MM) * SNAP_MM;
}

/** A figure without its unit — the customer's measure in „… (4.000 bis zum Eckpunkt)“. */
const FIGURE = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

/**
 * What a wall's figure says: its measure — or, on a wall a chamfer shortens,
 * both lengths the design names (2026-10-18 §5.1): what the gratings can lie
 * on, and the customer's measure up to the imagined corner point, which is
 * what the Maß field shows and changes. Read together they are
 * „3.500 mm belegbar (4.000 bis zum Eckpunkt)“; drawn, the second half takes
 * a line of its own — on one line the pair outgrew a short wall and ran into
 * its neighbour's figure (measured in the browser, 18.10.2026).
 */
function wallFigure(lengthMm: number, usable: number | null): { figure: string; toCorner: string | null } {
  if (usable === null || usable === lengthMm) return { figure: formatMm(lengthMm), toCorner: null };
  return { figure: `${formatMm(usable)} belegbar`, toCorner: `(${FIGURE.format(lengthMm)} bis zum Eckpunkt)` };
}

/** How far the second line of a figure stands beyond the first, outwards. */
const FIGURE_LINE_PX = 12;

/** The point a share `t` of the way from `a` to `b`. */
function along(a: ScreenPoint, b: ScreenPoint, t: number): ScreenPoint {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/** How far a slant's figure stands off the slant — a wall's 18 plus the room its text needs. */
const SLANT_FIGURE_OFFSET = DIMENSION_OFFSET + 6;

/** The reach of a corner's handle — its grab circle, the widest ring drawn there. */
const CORNER_HANDLE_RADIUS = 11;

/** Half a line of figure text (11 px, `.dimension`: about 6 px) and air to
 * the handle's dashed focus ring. */
const FIGURE_HALF_LINE = 10;

/** How wide one character of a figure runs — 11 px mono, 0.6 em. */
const FIGURE_CHAR_PX = 6.6;

/**
 * Where a slant's figure stands and how it turns: halfway along, off the
 * slant on the side `outward` points to, and **along** it — turned by the
 * slant's own angle, folded into (−90°, 90°] so it never reads upside down.
 * The wall figures turn by 0° or −90° only (`dimensionLine`); a chamfer is the
 * one line on this board that is neither.
 *
 * At an outer corner the outside is where the imagined corner point is, and
 * a picked corner's handle sits on that point (screen check 18.10.2026: its
 * ring covered „1.155 mm“). Where the figure would come within the handle's
 * reach, it moves on along the same normal, past the corner point — further
 * outside, where nothing else is drawn.
 */
function slantFigure(
  from: ScreenPoint,
  to: ScreenPoint,
  outward: ScreenPoint,
  corner: ScreenPoint,
  text: string,
): { x: number; y: number; rotation: number } {
  let rotation = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
  if (rotation > 90) rotation -= 180;
  if (rotation <= -90) rotation += 180;
  const middle = along(from, to, 0.5);
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  // The corner point in the figure's own frame: how far out, how far along.
  const out = (corner.x - middle.x) * outward.x + (corner.y - middle.y) * outward.y;
  const sideways = Math.abs(
    ((corner.x - middle.x) * (to.x - from.x) + (corner.y - middle.y) * (to.y - from.y)) / length,
  );
  const reach = CORNER_HANDLE_RADIUS + FIGURE_HALF_LINE;
  const besideTheText = sideways < (text.length * FIGURE_CHAR_PX) / 2 + CORNER_HANDLE_RADIUS;
  const offset =
    besideTheText && Math.abs(SLANT_FIGURE_OFFSET - out) < reach ? out + reach : SLANT_FIGURE_OFFSET;
  return {
    x: middle.x + outward.x * offset,
    y: middle.y + outward.y * offset,
    rotation,
  };
}

/**
 * What a gesture in flight is holding on to.
 *
 * Every frame recomputes from `base` rather than from the draft it produced
 * last frame. That is what makes `pullCorner` usable at all: the first frame
 * turns two walls into four, and a second pull applied on top of that result
 * would notch the notch. Starting from the shape as it was when the pointer
 * went down means the gesture stays one gesture, however long it lasts.
 */
interface Drag {
  kind: "wall" | "corner";
  index: number;
  startX: number;
  startY: number;
  base: Draft;
  /** The frame the board was fitted to when the drag began — see `frame`. */
  baseBounds: Bounds;
}

/**
 * The surface an outline is drawn on by hand.
 *
 * Holds points and shows them. Every statement about the shape — how many
 * walls there are, which way they run, what happens to a number when its wall
 * is split — is made in `lib/draft.ts`, where it is tested without a browser
 * (design §4.1, and the working rule from 10.08.2026: keine Fachaussagen in
 * der Darstellungsschicht).
 *
 * No millimetre drawn here is a measurement. A wall's figure is shown only
 * where somebody typed one; what the mouse produced lives in the coordinates
 * and is settled by the backend, which alone decides what an undrawn length is
 * worth (A-014).
 */
export function OutlineDraft({
  draft,
  onDraft,
  onGestureEnd,
  selectedWall,
  onSelectWall,
  selectedCorner = null,
  onSelectCorner = () => undefined,
  cornerHintId,
  underlay = null,
}: {
  draft: Draft;
  /**
   * `live` is true for every frame of a gesture after its first — the screen
   * uses it to fold a drag's hundred frames into one step on the undo stack,
   * so Rückgängig undoes the gesture and not a pixel of it.
   */
  onDraft: (next: Draft, live: boolean, taken?: () => void) => void;
  /**
   * The drag that just ended had moved something, and `base` is the shape it
   * started from — every frame of it is already a step (`onDraft`), so this is
   * where the screen asks about what the whole gesture took (spec 2026-10-28
   * §3.4). A drag that moved nothing does not call it.
   */
  onGestureEnd?: (base: Draft) => void;
  /** The wall the side panel is working on, or `null`. */
  selectedWall: number | null;
  onSelectWall: (index: number | null) => void;
  /**
   * The corner the side panel is working on, or `null` (Kauf-Test
   * 11.10.2026: a corner had no way to be taken out, because it could not
   * even be picked). A click that does not drag picks it, and so does focus.
   */
  selectedCorner?: number | null;
  onSelectCorner?: (index: number | null) => void;
  /** Where the side panel says what „Ecke entfernen“ does at the picked corner, or why not. */
  cornerHintId?: string;
  /**
   * A page to lie under the outline, 1:1 (plan 4 §5): its size in the
   * draft's own millimetres, its top left at the draft's origin. Only shown
   * — the board fits the outline, never the sheet, and nothing on the page
   * is read here.
   */
  underlay?: { href: string; widthMm: number; heightMm: number } | null;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  /**
   * A wall a keyboard gesture merged: the focused handle's own wall is gone,
   * and the handle that stands at its index now belongs to another one. The
   * focus goes to the merged wall once it is drawn.
   */
  const refocus = useRef<number | null>(null);
  useEffect(() => {
    const index = refocus.current;
    if (index === null) return;
    refocus.current = null;
    svgRef.current?.querySelector<SVGElement>(`[data-role="wall-handle"][data-wall="${index}"]`)?.focus();
  });
  /** Whether the gesture in flight has already produced a step to overwrite. */
  const emitted = useRef(false);
  const [boardWidth, setBoardWidth] = useState(BOARD_FALLBACK_WIDTH_PX);
  const [measuredHeight, setMeasuredHeight] = useState(0);
  const boardHeight = measuredHeight > 0 ? measuredHeight : BOARD_HEIGHT_PX;

  /**
   * The outline is fitted to the box the board has — and only to a box it
   * can draw in, as the planning board (UX review 26.10.2026, B-5; fix round
   * 1 of Task 10). A full-page capture lays the page out once at a 1 × 1
   * window: the box measured its padding and nothing inside it (and the
   * stage's height its 420 px floor), and the outline came out as a dot. A
   * box with no room inside the padding on either side is no box to fit to,
   * as one of no size is not: the board keeps the last one, whole, and fits
   * again with the next. Read once before the first paint, then followed —
   * the observer answers after the paint, and the drawing stood fitted to
   * the fallback box for a frame.
   */
  useLayoutEffect(() => {
    const element = svgRef.current;
    if (!element) return;
    const take = (width: number, height: number) => {
      if (width <= 2 * VIEW_PADDING || height <= 2 * VIEW_PADDING) return;
      setBoardWidth(width);
      setMeasuredHeight(height);
    };
    // The content box, as the observer reports it: without the board's edge.
    take(element.clientWidth, element.clientHeight);
    // jsdom has no ResizeObserver and no layout; the fallback box is what
    // the component tests measure against, and it is a real one.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      take(entry?.contentRect.width ?? 0, entry?.contentRect.height ?? 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // While a gesture runs the board is fitted to the shape it started from,
  // grown to hold whatever it has become. Refitting to the live shape every
  // frame would slide the drawing out from under the pointer — the wall would
  // chase the mouse instead of following it.
  const liveBounds = boundsOf(draft.vertices);
  const frame = drag ? union(drag.baseBounds, liveBounds) : liveBounds;

  const nodes: MmPoint[] = draft.vertices.map((vertex, index) => ({
    id: `v${index}`,
    xMm: vertex.xMm,
    yMm: vertex.yMm,
  }));
  // Two phantom corners hold the fit open to `frame`; they are never drawn.
  const projection = projectRun(
    [
      ...nodes,
      { id: "frame-min", xMm: frame.minX, yMm: frame.minY },
      { id: "frame-max", xMm: frame.maxX, yMm: frame.maxY },
    ],
    { width: boardWidth, height: boardHeight, padding: VIEW_PADDING },
  );
  const at = (index: number): ScreenPoint => projection.points[`v${index}`]!;

  /**
   * Client pixels → millimetres.
   *
   * One line, and that is the point of measuring the board: the viewBox is
   * written in the board's own pixels, so there is no viewBox-to-screen ratio
   * left to get wrong — only the projection's own millimetres per pixel.
   */
  function toMm(deltaClientPx: number): number {
    return deltaClientPx / projection.scale;
  }

  function beginDrag(
    event: ReactPointerEvent<SVGElement>,
    kind: Drag["kind"],
    index: number,
  ): void {
    // Best effort: a pointer id the browser does not consider active — a
    // synthetic event from a test harness, a stylus that lifted between the
    // two calls — throws here, and losing capture is not a reason to lose the
    // gesture. Without capture the drag still works while the pointer is over
    // the board.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* no capture; the gesture runs without it */
    }
    emitted.current = false;
    setDrag({
      kind,
      index,
      startX: event.clientX,
      startY: event.clientY,
      base: draft,
      baseBounds: boundsOf(draft.vertices),
    });
  }

  function moveDrag(event: ReactPointerEvent<SVGElement>): void {
    if (!drag) return;
    const dxMm = snap(toMm(event.clientX - drag.startX));
    const dyMm = snap(toMm(event.clientY - drag.startY));

    if (drag.kind === "wall") {
      const from = drag.base.vertices[drag.index]!;
      const to = drag.base.vertices[drag.index + 1]!;
      const alongY = from.yMm === to.yMm;
      const deltaMm = alongY ? dyMm : dxMm;
      const next = pushWallTracked(drag.base, drag.index, deltaMm, {
        snapMm: toMm(FLUSH_SNAP_PX),
      });
      // The wall is at its limit: nothing to record. Not so back at the start
      // (no push at all): the frame before it has to go, or the shape stays
      // where the pointer was — and a figure that came back would still be
      // asked about (`onGestureEnd`).
      if (next.draft === drag.base && !(deltaMm === 0 && emitted.current)) return;
      onDraft(next.draft, emitted.current);
      emitted.current = true;
      // Pushed flush, the wall in hand is the one it merged into — and back
      // off the line, itself again: every frame starts from `base`. The
      // handles are keyed by index, so the one the pointer pressed (and that
      // holds the focus and the capture) now stands for another wall: the
      // focus follows the wall, the capture stays where the gesture began.
      if (next.wall !== selectedWall) {
        onSelectWall(next.wall);
        if (svgRef.current?.contains(document.activeElement)) refocus.current = next.wall;
      }
      return;
    }

    const corner = drag.base.vertices[drag.index]!;
    const pulledTo = { xMm: corner.xMm + dxMm, yMm: corner.yMm + dyMm };
    // A loose end of an open walk has no corner to notch: pulled, it grows
    // the walk by one wall instead (19.10.2026). Every frame from `base`, so
    // the drag ends with one wall, not one per pointer move.
    const next = drag.base.closed
      ? pullCorner(drag.base, drag.index, pulledTo)
      : drag.index === 0 || drag.index === drag.base.vertices.length - 1
        ? appendWall(drag.base, drag.index, pulledTo)
        : pullCorner(drag.base, drag.index, pulledTo);
    if (next === drag.base) return; // no notch yet — the pointer is still on an axis
    onDraft(next, emitted.current);
    emitted.current = true;
  }

  function endDrag(event: ReactPointerEvent<SVGElement>): void {
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      /* never captured; nothing to release */
    }
    // A corner pressed and let go without pulling a notch is picked.
    if (drag?.kind === "corner" && !emitted.current) onSelectCorner(drag.index);
    if (drag && emitted.current) onGestureEnd?.(drag.base);
    setDrag(null);
  }

  function nudge(event: KeyboardEvent<SVGElement>, wallIndex: number): void {
    const from = draft.vertices[wallIndex]!;
    const to = draft.vertices[wallIndex + 1]!;
    const alongY = from.yMm === to.yMm;
    const step =
      event.key === (alongY ? "ArrowDown" : "ArrowRight")
        ? STEP_MM
        : event.key === (alongY ? "ArrowUp" : "ArrowLeft")
          ? -STEP_MM
          : null;

    if (step === null) {
      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        onDraft(deleteWall(draft, wallIndex), false);
        onSelectWall(null);
      }
      return;
    }
    event.preventDefault();
    const next = pushWallTracked(draft, wallIndex, step);
    if (next.draft === draft) return;
    // A key press is a whole step, so the screen may hold it for a question
    // (`taken` is what follows once it counts). A merged wall is picked and
    // focused only then: held, the shape on the board is still the old one.
    onDraft(next.draft, false, () => {
      if (next.wall === wallIndex) return;
      onSelectWall(next.wall);
      refocus.current = next.wall;
    });
  }

  /** Entf/Rücktaste on a corner: out, where its two walls run on one line; the side panel says why not otherwise. */
  function cornerKey(event: KeyboardEvent<SVGElement>, vertexIndex: number): void {
    if (event.key !== "Delete" && event.key !== "Backspace") return;
    event.preventDefault();
    if (cornerRemoval(draft, vertexIndex) !== "ok") return;
    const next = removeCorner(draft, vertexIndex);
    onDraft(next.draft, false);
    onSelectWall(next.wall);
    refocus.current = next.wall;
  }

  // A closed walk stores its start corner twice; only one of the two gets a
  // handle, or the same corner would answer to two controls.
  const cornerCount = draft.closed ? draft.vertices.length - 1 : draft.vertices.length;

  // ── Chamfers (design 2026-10-18 §6.5) ──────────────────────────────────
  // The draft stays axis-parallel; a chamfer is two legs on a corner, and is
  // drawn here as the corner cut off — the two walls stop where the slant
  // begins, the slant joins them. Every handle still sits on the shape the
  // gestures edit: the corner's on the imagined corner point, the wall's
  // along the whole wall.
  const chamfers = draft.chamfers ?? [];
  const wallCount = draft.walls.length;
  /** Every wall a person counts: the slants are walls of their own (`displayWallNumber`). */
  const wallTotal = wallCount + chamfers.length;
  const chamferAtEnd = new Map<number, DraftChamfer>(chamfers.map((chamfer) => [chamfer.wallIndex, chamfer]));
  const wallBefore = (index: number) => (draft.closed ? (index - 1 + wallCount) % wallCount : index - 1);
  const wallAfter = (index: number) => (draft.closed ? (index + 1) % wallCount : index + 1);

  /**
   * The share of wall `index` a leg takes — **by the wall's figure**, not by
   * the drawing: a sketch is not to scale until it is calibrated, and a leg
   * of 500 on a wall of 4.000 is an eighth of it whatever the pencil did.
   */
  function share(index: number, legMm: number): number {
    const from = draft.vertices[index]!;
    const to = draft.vertices[index + 1]!;
    const basis = draft.walls[index]!.lengthMm ?? Math.hypot(to.xMm - from.xMm, to.yMm - from.yMm);
    return basis > 0 ? Math.min(1, Math.max(0, legMm / basis)) : 0;
  }

  /** The drawn part of wall `index` — up to where a slant begins, at either end. */
  function visibleWall(index: number): [ScreenPoint, ScreenPoint] {
    const from = at(index);
    const to = at(index + 1);
    const atStart = chamferAtEnd.get(wallBefore(index));
    const atEnd = chamferAtEnd.get(index);
    return [
      along(from, to, atStart ? share(index, atStart.legBMm) : 0),
      along(from, to, 1 - (atEnd ? share(index, atEnd.legAMm) : 0)),
    ];
  }

  // Which side of a slant is outside, for its figure. On a ring the walk's own
  // turning sense says it — a chamfer at an inner corner has its outside on
  // the far side from the imagined corner point, one at an outer corner on
  // the near side, and the middle of the shape cannot tell the two apart. An
  // open chain has no inside: there the figure goes away from the middle, as
  // every wall figure does.
  let twiceArea = 0;
  for (let index = 0; index < wallCount; index += 1) {
    const a = at(index);
    const b = at(index + 1);
    twiceArea += a.x * b.y - b.x * a.y;
  }
  const turning = draft.closed ? Math.sign(twiceArea) : 0;
  function outwardOf(from: ScreenPoint, to: ScreenPoint): ScreenPoint {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy) || 1;
    if (turning !== 0) return { x: (turning * dy) / length, y: (-turning * dx) / length };
    const normal = { x: -dy / length, y: dx / length };
    const middle = along(from, to, 0.5);
    const away =
      (middle.x - projection.centroid.x) * normal.x + (middle.y - projection.centroid.y) * normal.y >= 0;
    return away ? normal : { x: -normal.x, y: -normal.y };
  }

  /**
   * A wall's figure as the board writes it — the number without „mm“
   * (20.10.2026), and at a chamfer the usable stretch with the customer's
   * figure to the corner point as a second line (E-63, design §6.5).
   */
  function boardFigures(index: number): { main: string; toCorner: string | null } | null {
    const lengthMm = draft.walls[index]!.lengthMm;
    if (lengthMm === null) return null;
    const parts = wallFigure(lengthMm, usableMm(draft, index));
    return { main: parts.figure.replace(/\s*mm(?= |$)/, ""), toCorner: parts.toCorner };
  }

  // Every figure dimensioned like a technical drawing (20.10.2026): a line
  // outside its wall, placed so that no two figures and no figure and wall
  // meet. Walls without a figure get no line.
  const walkPoints = draft.vertices.map((_, index) => at(index));
  const dimensionPlans = new Map<number, DimensionPlan>(
    placeDimensions(
      walkPoints,
      draft.closed,
      draft.walls.flatMap((wall, index) => {
        const figures = boardFigures(index);
        if (figures === null) return [];
        const seenHere = wall.readMm != null && wall.readMm === wall.lengthMm;
        // Up to where a slant begins: the line dimensions what is drawn.
        const [shownFrom, shownTo] = visibleWall(index);
        const longest = Math.max(figures.main.length, figures.toCorner?.length ?? 0);
        return [
          {
            index,
            from: shownFrom,
            to: shownTo,
            textWidth: longest * DIGIT_PX + (seenHere ? SEEN_DOT_PX : 0),
          },
        ];
      }),
    ).map((plan) => [plan.index, plan]),
  );

  return (
    // The drawing is a sheet, paper in the dark too (design 2026-10-24
    // §5.3) — a drawing of ours, on warm paper (B-20); the tools beside it
    // follow the mode (§8.3).
    <svg
      ref={svgRef}
      className={styles.board}
      data-blatt="zeichnung"
      viewBox={`0 0 ${boardWidth} ${boardHeight}`}
      role="group"
      aria-label={`Umriss zeichnen, ${wallTotal} Wände, ${draft.closed ? "geschlossener Umlauf" : "offener Zug"}`}
      data-closed={draft.closed}
      data-walls={draft.walls.length}
      data-underlay={underlay !== null || undefined}
    >
      {underlay !== null && draft.vertices[0] !== undefined ? (
        <image
          className={styles.underlay}
          data-role="draft-underlay"
          href={underlay.href}
          x={at(0).x - draft.vertices[0].xMm * projection.scale}
          y={at(0).y - draft.vertices[0].yMm * projection.scale}
          width={underlay.widthMm * projection.scale}
          height={underlay.heightMm * projection.scale}
          preserveAspectRatio="none"
          aria-hidden="true"
        />
      ) : null}
      {draft.walls.map((wall, index) => {
        // The whole wall for its handle — the gestures edit the axis-parallel
        // shape — and the part up to a slant for everything drawn.
        const from = at(index);
        const to = at(index + 1);
        const [shownFrom, shownTo] = visibleWall(index);
        const read = wall.lengthMm !== null;
        // Visual only (plan 4 §5, D-1): the figure is still the one the
        // reading found — nothing on the wire says so, the editor sends it as
        // a person's.
        const seen = read && wall.readMm != null && wall.readMm === wall.lengthMm;
        const alongY = draft.vertices[index]!.yMm === draft.vertices[index + 1]!.yMm;
        const plan = dimensionPlans.get(index) ?? null;
        const parts = read ? wallFigure(wall.lengthMm!, usableMm(draft, index)) : null;
        const figure = parts === null ? "kein Maß" : [parts.figure, parts.toCorner].filter(Boolean).join(" ");
        const figures = boardFigures(index);
        // Same line, pushed the other way: a negative offset lands inside the
        // shape instead of outside it.
        const inward = dimensionLine(shownFrom, shownTo, projection.centroid, -LANDMARK_OFFSET);

        return (
          <g key={index} data-role="draft-wall" data-wall={index} data-read={read} data-seen={seen || undefined}>
            {selectedWall === index ? (
              <line
                className={styles.wallSelected}
                x1={shownFrom.x}
                y1={shownFrom.y}
                x2={shownTo.x}
                y2={shownTo.y}
              />
            ) : null}
            <line
              className={read ? styles.wallRead : styles.wallSketch}
              data-role="draft-wall-line"
              x1={shownFrom.x}
              y1={shownFrom.y}
              x2={shownTo.x}
              y2={shownTo.y}
            />
            {/* Only where somebody typed one. A wall drawn from a drag carries
                no figure, because there is no figure to carry (design §2). */}
            {read && plan !== null ? (
              <g data-role="draft-dimension-set" aria-hidden="true">
                <line className={styles.dimensionLine} {...plan.line} />
                {plan.leader !== null ? <line className={styles.dimensionLine} {...plan.leader} /> : null}
                {plan.extensions.map((extension, end) => (
                  <line key={end} className={styles.extensionLine} {...extension} />
                ))}
                {/* End strokes at 45°, as on a drawing — no arrowheads. */}
                {[
                  [plan.line.x1, plan.line.y1],
                  [plan.line.x2, plan.line.y2],
                ].map(([x, y], end) => (
                  <line
                    key={end}
                    className={styles.dimensionTick}
                    x1={x! - TICK_PX}
                    y1={y! + TICK_PX}
                    x2={x! + TICK_PX}
                    y2={y! - TICK_PX}
                  />
                ))}
                <text
                  className={styles.dimension}
                  data-role="draft-dimension"
                  x={plan.label.x}
                  y={plan.label.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={plan.label.rotation ? `rotate(${plan.label.rotation} ${plan.label.x} ${plan.label.y})` : undefined}
                >
                  {seen ? (
                    // Visual only (D-1): read, not stated — the slider says
                    // „gelesen“ in words for a screen reader.
                    <tspan className={styles.seen} data-role="draft-seen">
                      {"● "}
                    </tspan>
                  ) : null}
                  {figures?.main}
                  {figures?.toCorner ? (
                    // The second line goes further out, never back over the
                    // wall: „down“ in a turned figure's frame is screen right.
                    <tspan
                      className={styles.toCorner}
                      x={plan.label.x}
                      dy={
                        (plan.label.rotation
                          ? Math.sign(plan.label.x - (plan.line.x1 + plan.line.x2) / 2)
                          : Math.sign(plan.label.y - (plan.line.y1 + plan.line.y2) / 2)) * FIGURE_LINE_PX || FIGURE_LINE_PX
                      }
                    >
                      {` ${figures.toCorner}`}
                    </tspan>
                  ) : null}
                </text>
              </g>
            ) : null}
            {/* Inside the shape, where the figure never goes — the two used to
                land on the same spot and print over each other (measured in the
                browser, 15.08.2026). A place name also stays upright while the
                figure turns with its wall: „Haustüre" is read, not measured. */}
            {wall.label ? (
              <text
                className={styles.landmark}
                data-role="draft-landmark"
                x={inward.labelX}
                y={inward.labelY}
                // On an upright wall the name is set *away* from the wall
                // rather than centred on a point beside it: „Terrassentür" is
                // wider than the gap between the wall and its own figure, and
                // centred it grew back across both (measured in the browser,
                // 15.08.2026). Anchoring it by its near edge makes its width
                // irrelevant.
                textAnchor={
                  alongY ? "middle" : inward.labelX < (from.x + to.x) / 2 ? "end" : "start"
                }
                dominantBaseline="middle"
              >
                {wall.label}
              </text>
            ) : null}
            <line
              className={`${styles.grip} ${alongY ? styles.gripNs : styles.gripEw}`}
              data-role="wall-handle"
              data-wall={index}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              strokeWidth={GRAB_HALF_WIDTH * 2}
              role="slider"
              tabIndex={0}
              aria-label={`Wand ${displayWallNumber(draft, index)} von ${wallTotal}, ${figure}${
                seen ? ", gelesen" : ""
              }, ${alongY ? "waagerecht" : "senkrecht"}`}
              aria-orientation={alongY ? "vertical" : "horizontal"}
              aria-valuenow={read ? wall.lengthMm! : undefined}
              aria-valuetext={figure}
              onFocus={() => onSelectWall(index)}
              onPointerDown={(event) => {
                onSelectWall(index);
                beginDrag(event, "wall", index);
              }}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onKeyDown={(event) => nudge(event, index)}
            />
          </g>
        );
      })}

      {chamfers.map((chamfer) => {
        const next = wallAfter(chamfer.wallIndex);
        if (chamfer.wallIndex < 0 || chamfer.wallIndex >= wallCount || next >= wallCount) return null;
        // Where the slant begins on its wall, and where it ends on the next.
        const start = visibleWall(chamfer.wallIndex)[1];
        const end = visibleWall(next)[0];
        const corner = at(chamfer.wallIndex + 1);
        const length = diagonalMm(chamfer.legAMm, chamfer.legBMm);
        const label = slantFigure(start, end, outwardOf(start, end), corner, formatMm(length));
        return (
          <g
            key={`chamfer-${chamfer.wallIndex}`}
            data-role="draft-chamfer"
            data-wall={chamfer.wallIndex}
            role="img"
            aria-label={`Wand ${chamferWallNumber(draft, chamfer.wallIndex)} von ${wallTotal}, Schräge ${formatMm(length)}`}
          >
            {/* The imagined corner point the walls are measured to — thin and
                dashed: a construction line, not a wall. */}
            <polyline
              className={styles.chamferGhost}
              points={`${start.x},${start.y} ${corner.x},${corner.y} ${end.x},${end.y}`}
            />
            <line
              className={styles.wallRead}
              data-role="draft-chamfer-line"
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
            />
            <text
              className={styles.dimension}
              data-role="draft-chamfer-dimension"
              x={label.x}
              y={label.y}
              textAnchor="middle"
              dominantBaseline="middle"
              transform={`rotate(${label.rotation} ${label.x} ${label.y})`}
            >
              {formatMm(length).replace(/\s*mm$/, "")}
            </text>
          </g>
        );
      })}

      {Array.from({ length: cornerCount }, (_, index) => {
        const point = at(index);
        const picked = selectedCorner === index;
        const chamfer = chamferAtEnd.get(wallBefore(index));
        return (
          <g key={`corner-${index}`} data-role="draft-corner" data-corner={index} data-selected={picked || undefined}>
            {picked ? <circle className={styles.cornerSelected} cx={point.x} cy={point.y} r={9} /> : null}
            <circle className={styles.corner} cx={point.x} cy={point.y} r={4} />
            <circle
              className={styles.cornerGrab}
              data-role="corner-handle"
              data-corner={index}
              cx={point.x}
              cy={point.y}
              r={CORNER_HANDLE_RADIUS}
              role="button"
              tabIndex={0}
              aria-label={`Ecke ${index + 1} von ${cornerCount}${
                chamfer ? `, Fase ${formatMm(diagonalMm(chamfer.legAMm, chamfer.legBMm))}` : ""
              }`}
              aria-pressed={picked}
              aria-describedby={picked ? cornerHintId : undefined}
              onFocus={() => onSelectCorner(index)}
              onKeyDown={(event) => cornerKey(event, index)}
              onPointerDown={(event) => beginDrag(event, "corner", index)}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <title>Ecke {index + 1} — anklicken zum Wählen, hineinziehen für eine neue Kante</title>
            </circle>
          </g>
        );
      })}

      {/* Where the drawing breaks off, on an open walk. */}
      {!draft.closed
        ? [0, draft.walls.length].map((vertexIndex) => {
            const point = at(vertexIndex);
            return (
              <circle
                key={`open-${vertexIndex}`}
                className={styles.openEnd}
                data-role="draft-open-end"
                cx={point.x}
                cy={point.y}
                r={8}
              />
            );
          })
        : null}
    </svg>
  );
}
